import I18n from 'i18n-js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import Utils from '../../utils/a11yHelper.js';
import CircularProgress from '@material-ui/core/CircularProgress';
import instanceHelper from '../../utils/eyesonInstanceHelper.js';
import tabAudioHelper from '../../utils/eyesonTabAudioHelper.js';
import secondCamHelper from '../../utils/eyesonSecondCamHelper.js';
import ScreenShareIcon from '@material-ui/icons/ScreenShare';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import Chip from '@material-ui/core/Chip';
import supports from '../../utils/SupportHelper.js';
import { red } from '@material-ui/core/colors';
import { ReactComponent as ScreenshareFullIcon } from '../../assets/share-screen-full.svg';
import { ReactComponent as ScreenshareSplitIcon } from '../../assets/share-screen-split.svg';
import { ReactComponent as ScreenshareReplaceIcon } from '../../assets/share-screen-replace.svg';
import { ReactComponent as ShareTabAudioIcon } from '../../assets/share-tab-audio.svg';
import { ReactComponent as ShareYoutubeIcon } from '../../assets/share-youtube.svg';
import { ReactComponent as ShareLocalFileIcon } from '../../assets/share-local-file.svg';
import { ReactComponent as ShareSecondCameraIcon } from '../../assets/share-second-camera.svg';
import classNames from 'classnames';
import { DialogContentText } from '@material-ui/core';

const usePresentationDialogStyles = makeStyles((theme) => ({
  root: {
    marginBlockEnd: '-8px',
    '& .feature': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '8px',
      padding: '16px 24px',
      '& .name': {
        flex: '1 1 0%',
        minWidth: 'fit-content',
        '&.withIcon': {
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        },
      },
      '& .actions': {
        display: 'flex',
        gap: '8px',
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
      },
    },
    '& .feature:not(:last-child)': {
      borderBottom: '1px solid rgba(0, 0, 0, .125)',
    },
    '& .feature.disabled': {
      color: theme.palette.text.disabled,
    },
    '& .presentation-hint': {
      marginBlock: '1rem',
    },
    '& #share-dialog-desc': {
      display: 'none',
    },
  },
  bigIcon: {
    '--svg-bg-color': theme.palette.text.primary,
    '--svg-bg-opacity': 0.3,
    '--svg-arrow-color': '#fff',
    width: '5rem',
    color: theme.palette.secondary.main,
    '& svg': {
      verticalAlign: 'middle',
    },
    '.feature.disabled &': {
      '--svg-bg-color': theme.palette.action.disabled,
      color: theme.palette.action.disabled,
      '--svg-arrow-color': theme.palette.text.disabled,
    },
  },
  buttonSpinner: {
    marginInlineStart: '0.5rem',
    verticalAlign: 'middle',
  },
}));

const Accordion = withStyles({
  root: {
    overflow: 'clip',
    marginInline: '-24px',
    borderBlock: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: '0 -24px',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    paddingInline: '24px 20px',
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '& svg': {
      marginRight: '0.75rem',
    },
    '&$expanded': {
      margin: '12px 0',
    },
    '& .MuiChip-root': {
      marginInlineStart: 'auto',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    display: 'block',
    overflow: 'clip',
    padding: 0,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(0, 0, 0, .08)'
        : 'rgba(0, 0, 0, .04)',
  },
}))(MuiAccordionDetails);

const StopButton = withStyles({
  root: {
    color: red[500],
    borderColor: red[500],
    '&:hover': {
      color: red[700],
      borderColor: red[700],
    },
  },
})(Button);

const ScreenCaptureHint = ({ eco, environment }) => {
  if (!environment.canScreenCapture) {
    return (
      <Typography variant="body2" component="p" className="presentation-hint">
        {I18n.t('dialog:presentation:screen_capture_not_supported')}
      </Typography>
    );
  }
  if (eco) {
    return (
      <Typography variant="body2" component="p" className="presentation-hint">
        {I18n.t('dialog:presentation:eco')}
      </Typography>
    );
  }
  return null;
};

const PresentationDialog = ({
  dialogId,
  environment,
  eco,
  onEvent,
  tabAudio,
  screenAsVideo,
  screenAddition,
  quickJoin,
  isLocked,
  activeYoutubePopup,
  hasPresenter,
  screenCapture,
  options,
  recvOnlyMode,
}) => {
  const screenCaptureDisabled = !environment.canScreenCapture || eco;
  const filePresentationDisabled = !environment.canPresentFiles || eco;
  const tabAudioSupported = quickJoin && tabAudioHelper.checkSupport();
  const secondCameraSupported = supports.SecondCamera && quickJoin;
  const screenShareActive = hasPresenter || screenAddition || screenAsVideo;
  const secondCamActive =
    secondCameraSupported && secondCamHelper.getCurrentState() === 'active';
  const shareMediaActive = secondCamActive || activeYoutubePopup || tabAudio;
  const screenShareFullDisabled =
    screenCaptureDisabled ||
    screenAsVideo ||
    screenAddition ||
    secondCamActive ||
    recvOnlyMode;
  const screenShareSplitDisabled =
    hasPresenter ||
    (quickJoin &&
      (isLocked ||
        screenCaptureDisabled ||
        instanceHelper.getCurrentState() === 'init'));
  const screenShareReplaceDisabled =
    hasPresenter || screenCaptureDisabled || recvOnlyMode;
  const secondCameraDisabled =
    hasPresenter || (secondCameraSupported && (isLocked || eco));
  const youtubeDisabled =
    hasPresenter ||
    screenCaptureDisabled ||
    (supports.YoutubePopup && (isLocked || eco));
  const tabAudioDisabled =
    hasPresenter ||
    (tabAudioSupported &&
      (isLocked ||
        screenCaptureDisabled ||
        tabAudioHelper.getCurrentState() === 'init'));
  const filePresentFeatureDisabled =
    hasPresenter ||
    filePresentationDisabled ||
    screenAsVideo ||
    screenAddition ||
    recvOnlyMode;
  const dialogClasses = usePresentationDialogStyles();
  const preselect = shareMediaActive ? 'media' : 'screen';

  const [expanded, setExpanded] = useState(preselect);

  const expandAccordion = (panel) => () => {
    setExpanded(panel);
  };

  return (
    <div className={dialogClasses.root}>
      <DialogContentText id={dialogId + '-desc'} tabIndex={0}>
        {I18n.t('dialog:subtitle:presentation')}
      </DialogContentText>
      <Accordion
        square
        expanded={expanded === 'screen'}
        onChange={expandAccordion('screen')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="presentation-screen-content"
        >
          <ScreenShareIcon />
          <Typography>
            {I18n.t('dialog:presentation:section:share_screen')}
          </Typography>
          {screenShareActive && (
            <Chip
              label={I18n.t('label:active')}
              color="secondary"
              size="small"
            ></Chip>
          )}
        </AccordionSummary>
        <AccordionDetails id="presentation-screen-content">
          <div
            className={classNames('feature', {
              disabled: screenShareFullDisabled,
            })}
          >
            <div className="name withIcon">
              <div className={dialogClasses.bigIcon}>
                <ScreenshareFullIcon />
              </div>
              <div>
                <Typography variant="overline">
                  {I18n.t('dialog:presentation:share_screen_full:title')}
                </Typography>
                <Typography variant="body2">
                  {I18n.t('dialog:presentation:share_screen_full:description')}
                </Typography>
              </div>
            </div>
            {hasPresenter ? (
              <div className="actions">
                {screenCapture && (
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() => {
                      onEvent({ type: 'change_screen_capture' });
                    }}
                  >
                    {I18n.t('action:change_screen')}
                  </Button>
                )}
                <StopButton
                  variant="outlined"
                  onClick={() => {
                    Utils.clearActiveElement();
                    onEvent({ type: 'stop_presentation' });
                    onEvent({ type: 'hide_dialog' });
                  }}
                >
                  {I18n.t('label:stop')}
                </StopButton>
              </div>
            ) : (
              <div className="actions">
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    Utils.clearActiveElement();
                    onEvent({ type: 'activate_screen_capture' });
                  }}
                  disabled={screenShareFullDisabled}
                >
                  {I18n.t('label:share')}
                </Button>
              </div>
            )}
          </div>
          {quickJoin && (
            <div
              className={classNames('feature', {
                disabled: screenShareSplitDisabled,
              })}
            >
              <div className="name withIcon">
                <div className={dialogClasses.bigIcon}>
                  <ScreenshareSplitIcon />
                </div>
                <div>
                  <Typography variant="overline">
                    {I18n.t('dialog:presentation:split_view:title')}
                  </Typography>
                  <Typography variant="body2">
                    {I18n.t('dialog:presentation:split_view:description')}
                  </Typography>
                  {isLocked && (
                    <Typography variant="caption" component="p">
                      {I18n.t(
                        'dialog:presentation:screen_additional_user_not_supported:locked'
                      )}
                    </Typography>
                  )}
                </div>
              </div>
              {screenAddition ? (
                <div className="actions">
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={async () => {
                      if (await instanceHelper.changeScreenshare()) {
                        onEvent({ type: 'hide_dialog' });
                      }
                    }}
                  >
                    {I18n.t('action:change_screen')}
                  </Button>
                  {options.layout_available && (
                    <Button
                      color="secondary"
                      variant="outlined"
                      disabled={instanceHelper.isCurrentPresentationLayout()}
                      onClick={() => {
                        instanceHelper.setPresentationLayout();
                        onEvent({ type: 'hide_dialog' });
                      }}
                    >
                      {I18n.t('action:presentation_mode')}
                    </Button>
                  )}
                  <StopButton
                    variant="outlined"
                    onClick={() => {
                      onEvent({ type: 'hide_dialog' });
                      onEvent({ type: 'screen_addition_stop' });
                    }}
                  >
                    {I18n.t('label:stop')}
                  </StopButton>
                </div>
              ) : (
                <div className="actions">
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      Utils.clearActiveElement();
                      onEvent({ type: 'screen_as_addition' });
                    }}
                    disabled={screenShareSplitDisabled}
                  >
                    {I18n.t('label:share')}
                    {instanceHelper.getCurrentState() === 'init' && (
                      <CircularProgress
                        color="inherit"
                        size="1.2rem"
                        className={dialogClasses.buttonSpinner}
                      />
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
          <div
            className={classNames('feature', {
              disabled: screenShareReplaceDisabled,
            })}
          >
            <div className="name withIcon">
              <div className={dialogClasses.bigIcon}>
                <ScreenshareReplaceIcon />
              </div>
              <div>
                <Typography variant="overline">
                  {I18n.t('dialog:presentation:share_screen_replace:title')}
                </Typography>
                <Typography variant="body2">
                  {I18n.t(
                    'dialog:presentation:share_screen_replace:description'
                  )}
                </Typography>
              </div>
            </div>
            {screenAsVideo ? (
              <div className="actions">
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    onEvent({ type: 'hide_dialog' });
                    onEvent({ type: 'screen_as_video_change' });
                  }}
                >
                  {I18n.t('action:change_screen')}
                </Button>
                <StopButton
                  variant="outlined"
                  onClick={() => {
                    onEvent({ type: 'hide_dialog' });
                    onEvent({ type: 'screen_video_ended' });
                  }}
                >
                  {I18n.t('label:stop')}
                </StopButton>
              </div>
            ) : (
              <div className="actions">
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    onEvent({ type: 'hide_dialog' });
                    onEvent({ type: 'screen_as_video' });
                  }}
                  disabled={screenShareReplaceDisabled}
                >
                  {I18n.t('label:share')}
                </Button>
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
      {(secondCameraSupported ||
        (supports.YoutubePopup && quickJoin) ||
        tabAudioSupported) && (
        <Accordion
          square
          expanded={expanded === 'media'}
          onChange={expandAccordion('media')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="presentation-media-content"
          >
            <PlayCircleFilledIcon />
            <Typography>
              {I18n.t('dialog:presentation:section:share_media')}
            </Typography>
            {shareMediaActive && (
              <Chip
                label={I18n.t('label:active')}
                color="secondary"
                size="small"
              ></Chip>
            )}
          </AccordionSummary>
          <AccordionDetails id="presentation-media-content">
            {secondCameraSupported && (
              <div
                className={classNames('feature', {
                  disabled: secondCameraDisabled,
                })}
              >
                <div className="name withIcon">
                  <div className={dialogClasses.bigIcon}>
                    <ShareSecondCameraIcon />
                  </div>
                  <div>
                    <Typography variant="overline">
                      {I18n.t('dialog:presentation:second_camera:title')}
                    </Typography>
                    <Typography variant="body2">
                      {I18n.t('dialog:presentation:second_camera:description')}
                    </Typography>
                    {isLocked && (
                      <Typography variant="caption" component="p">
                        {I18n.t(
                          'dialog:presentation:screen_additional_user_not_supported:locked'
                        )}
                      </Typography>
                    )}
                  </div>
                </div>
                {secondCamActive ? (
                  <div className="actions">
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => {
                        onEvent({ type: 'show_second_camera_dialog' });
                      }}
                    >
                      {I18n.t('action:switch_camera')}
                    </Button>
                    {options.layout_available && (
                      <Button
                        color="secondary"
                        variant="outlined"
                        disabled={secondCamHelper.isCurrentPresentationLayout()}
                        onClick={() => {
                          secondCamHelper.setPresentationLayout();
                          onEvent({ type: 'hide_dialog' });
                        }}
                      >
                        {I18n.t('action:presentation_mode')}
                      </Button>
                    )}
                    <StopButton
                      variant="outlined"
                      onClick={() => {
                        onEvent({ type: 'hide_dialog' });
                        secondCamHelper.stop();
                      }}
                    >
                      {I18n.t('label:stop')}
                    </StopButton>
                  </div>
                ) : (
                  <div className="actions">
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={secondCameraDisabled}
                      onClick={() => {
                        Utils.clearActiveElement();
                        onEvent({ type: 'show_second_camera_dialog' });
                      }}
                    >
                      {I18n.t('label:start')}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {supports.YoutubePopup && quickJoin && (
              <div
                className={classNames('feature', { disabled: youtubeDisabled })}
              >
                <div className="name withIcon">
                  <div className={dialogClasses.bigIcon}>
                    <ShareYoutubeIcon />
                  </div>
                  <div>
                    <Typography variant="overline">
                      {I18n.t('dialog:presentation:share_youtube:title')}
                    </Typography>
                    <Typography variant="body2">
                      {I18n.t('dialog:presentation:share_youtube:description')}
                    </Typography>
                    {isLocked && (
                      <Typography variant="caption" component="p">
                        {I18n.t(
                          'dialog:presentation:screen_additional_user_not_supported:locked'
                        )}
                      </Typography>
                    )}
                  </div>
                </div>
                {activeYoutubePopup ? (
                  <div className="actions">
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => {
                        onEvent({ type: 'show_youtube_popup' });
                      }}
                    >
                      Show Popup
                    </Button>
                    <StopButton
                      variant="outlined"
                      onClick={() => {
                        onEvent({ type: 'close_youtube_popup' });
                      }}
                    >
                      {I18n.t('label:close')}
                    </StopButton>
                  </div>
                ) : (
                  <div className="actions">
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        Utils.clearActiveElement();
                        onEvent({ type: 'show_youtube_popup' });
                        onEvent({ type: 'hide_dialog' });
                      }}
                      disabled={youtubeDisabled}
                    >
                      {I18n.t('label:start')}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {tabAudioSupported && (
              <div
                className={classNames('feature', {
                  disabled: tabAudioDisabled,
                })}
              >
                <div className="name withIcon">
                  <div className={dialogClasses.bigIcon}>
                    <ShareTabAudioIcon />
                  </div>
                  <div>
                    <Typography variant="overline">
                      {I18n.t('dialog:presentation:share_tab_audio:title')}
                    </Typography>
                    <Typography variant="body2">
                      {I18n.t(
                        'dialog:presentation:share_tab_audio:description'
                      )}
                    </Typography>
                    {isLocked && (
                      <Typography variant="caption" component="p">
                        {I18n.t(
                          'dialog:presentation:tab_audio_not_supported:locked'
                        )}
                      </Typography>
                    )}
                  </div>
                </div>
                {tabAudio ? (
                  <div className="actions">
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={async () => {
                        if (await tabAudioHelper.changeScreenshare()) {
                          onEvent({ type: 'hide_dialog' });
                        }
                      }}
                    >
                      {I18n.t('action:switch_tab')}
                    </Button>
                    <StopButton
                      variant="outlined"
                      onClick={() => {
                        onEvent({ type: 'hide_dialog' });
                        onEvent({ type: 'stop_tab_audio' });
                      }}
                    >
                      {I18n.t('label:stop')}
                    </StopButton>
                  </div>
                ) : (
                  <div className="actions">
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        Utils.clearActiveElement();
                        onEvent({ type: 'start_tab_audio' });
                      }}
                      disabled={tabAudioDisabled}
                    >
                      {I18n.t('label:start')}
                      {tabAudioHelper.getCurrentState() === 'init' && (
                        <CircularProgress
                          color="inherit"
                          size="1.2rem"
                          className={dialogClasses.buttonSpinner}
                        />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        square
        expanded={expanded === 'file'}
        onChange={expandAccordion('file')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="presentation-file-content"
        >
          <InsertDriveFileIcon />
          <Typography>
            {I18n.t('dialog:presentation:section:present_file')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails id="presentation-file-content">
          <div
            className={classNames('feature', {
              disabled: filePresentFeatureDisabled,
            })}
          >
            <div className="name withIcon">
              <div className={dialogClasses.bigIcon}>
                <ShareLocalFileIcon />
              </div>
              <div>
                <Typography variant="overline">
                  {I18n.t('dialog:presentation:share_local_file:title')}
                </Typography>
                <Typography variant="body2">
                  {I18n.t('dialog:presentation:share_local_file:description')}
                </Typography>
              </div>
            </div>
            <div className="actions">
              <Button
                color="secondary"
                variant="contained"
                onClick={() => {
                  Utils.clearActiveElement();
                  onEvent({ type: 'show_drop_zone' });
                }}
                disabled={filePresentFeatureDisabled}
              >
                {I18n.t('label:start')}
              </Button>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <ScreenCaptureHint eco={eco} environment={environment} />
    </div>
  );
};

PresentationDialog.propTypes = {
  eco: PropTypes.bool,
  onEvent: PropTypes.func.isRequired,
  environment: PropTypes.object,
  quickJoin: PropTypes.bool,
  isLocked: PropTypes.bool,
  activeYoutubePopup: PropTypes.object,
  hasPresenter: PropTypes.bool,
  screenCapture: PropTypes.bool,
};

PresentationDialog.defaultProps = {
  eco: false,
  environment: { canScreenCapture: true, isIncongnito: false },
  quickJoin: true,
  isLocked: false,
  activeYoutubePopup: null,
  hasPresenter: false,
  screenCapture: false,
};

export default PresentationDialog;
